import { useMemo } from "react";
import { useFundFiltersMetadata } from "creso-core/hooks";
import {
  DistinctFilterMetadata,
  ExternalHoldings,
  PortfolioReview,
  PortfolioReviewLine,
  PortfolioReviewOverview,
  PortfolioReviewReport,
} from "creso-core/types";
import type { ReportPreviewSection } from "../report-preview";

export type ReportPosition = {
  name: string;
  amcCode: string | null;
  value: number;
};

export type RankedItem = {
  key: string;
  name: string;
  amcCode: string | null;
  pct: number;
};

// What the client holds today: every line except funds still to be bought.
export function positionsBefore(lines: PortfolioReviewLine[]) {
  return lines
    .filter((line) => line.bucket !== "NEW_ADDITION")
    .map<ReportPosition>((line) => ({
      name: line.source_scheme_name,
      amcCode: line.amc_morningstar_code,
      value: Number(line.current_value),
    }));
}

// Holds stay, exits leave, switches and additions arrive in their target.
export function positionsAfter(lines: PortfolioReviewLine[]) {
  return lines.flatMap<ReportPosition>((line) => {
    if (line.bucket === "HOLD") {
      return [
        {
          name: line.source_scheme_name,
          amcCode: line.amc_morningstar_code,
          value: Number(line.current_value),
        },
      ];
    }
    if (
      (line.bucket === "SWITCH" || line.bucket === "NEW_ADDITION") &&
      line.target_scheme_name
    ) {
      return [
        {
          name: line.target_scheme_name,
          amcCode: line.target_amc_morningstar_code,
          value: Number(line.amount ?? line.current_value),
        },
      ];
    }
    return [];
  });
}

function rank(groups: Map<string, RankedItem & { value: number }>, total = 0) {
  return [...groups.values()]
    .map(({ value, ...item }) => ({
      ...item,
      pct: total ? (value / total) * 100 : 0,
    }))
    .sort((a, b) => b.pct - a.pct);
}

const totalOf = (positions: ReportPosition[]) =>
  positions.reduce((sum, p) => sum + p.value, 0);

export function topFunds(positions: ReportPosition[], limit = 3) {
  const groups = new Map<string, RankedItem & { value: number }>();
  positions.forEach((p) => {
    const existing = groups.get(p.name);
    groups.set(p.name, {
      key: p.name,
      name: p.name,
      amcCode: p.amcCode,
      pct: 0,
      value: (existing?.value ?? 0) + p.value,
    });
  });
  return rank(groups, totalOf(positions)).slice(0, limit);
}

export function topAmcs(
  positions: ReportPosition[],
  amcName: (code: string | null) => string,
  limit = 3
) {
  const groups = new Map<string, RankedItem & { value: number }>();
  positions.forEach((p) => {
    const key = p.amcCode ?? "unknown";
    const existing = groups.get(key);
    groups.set(key, {
      key,
      name: amcName(p.amcCode),
      amcCode: p.amcCode,
      pct: 0,
      value: (existing?.value ?? 0) + p.value,
    });
  });
  return rank(groups, totalOf(positions)).slice(0, limit);
}

// Morningstar AMC code to fund house name, from the fund filter metadata,
// topped up with names the review already carries.
export function useAmcNames(review: PortfolioReview) {
  const { data } = useFundFiltersMetadata(["provider_company_name"]);
  return useMemo(() => {
    const names = new Map<string, string>();
    review.lines.forEach((line) => {
      if (line.amc_morningstar_code && line.amc_name) {
        names.set(line.amc_morningstar_code, line.amc_name);
      }
    });
    const providers = (data ?? []).find(
      (f) => f.type === "distinct" && f.field === "provider_company_name"
    ) as DistinctFilterMetadata | undefined;
    providers?.values.forEach(({ id, name }) =>
      id
        ?.split(",")
        .map((code) => code.trim())
        .filter(Boolean)
        .forEach((code) => names.set(code, name))
    );
    return (code: string | null) => (code && names.get(code)) || "Other AMCs";
  }, [data, review.lines]);
}

const REASON_TEXT: Record<string, string> = {
  LOW_QUARTILE: "Low 3Y quartile rank vs category peers",
  BELOW_BENCHMARK: "Underperforming its benchmark",
  SMALL_HOLDING: "Holding is too small to matter to the portfolio",
  SMALL_HOLDING_REVIEW: "Small holding worth reviewing",
  CATEGORY_TOO_THIN: "Too few funds in the category to rank against",
  CATEGORY_CROWDED: "Several funds held in the same category",
};

// The distributor's comment wins; otherwise the engine's reasons are spelt out.
export function lineReason(line: PortfolioReviewLine) {
  if (line.comment) return line.comment;
  // The engine stores reason codes as plain strings.
  const reasons: unknown[] = Array.isArray(line.reasons) ? line.reasons : [];
  const text = reasons
    .filter((r): r is string => typeof r === "string")
    .map((r) => REASON_TEXT[r])
    .filter(Boolean)
    .join(". ");
  if (line.bucket === "SWITCH" && line.target_scheme_name) {
    return [text, `Recommended switch to ${line.target_scheme_name}`]
      .filter(Boolean)
      .join(". ");
  }
  return text;
}

export function linesIn(
  review: PortfolioReview,
  bucket: PortfolioReviewLine["bucket"]
) {
  return review.lines
    .filter((line) => line.bucket === bucket)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function externalHoldingsOf(overview?: PortfolioReviewOverview | null) {
  const block = overview?.external_holdings;
  const holdings: ExternalHoldings | null = block?.ok ? block.data : null;
  return holdings && Number(holdings.external_aum) ? holdings : null;
}

// Sections the report has data for. The preview's checklist and the report
// page both read this, so a section can never be ticked but missing.
export function reviewReportSections({
  report,
}: {
  report?: PortfolioReviewReport | null;
}): ReportPreviewSection[] {
  const hasSeries = !!report?.overview.backtest.series.current.length;

  // One checkbox per section: overview also prints the external holdings
  // pages, and recommendations and exposure print every page they have data for.
  return [
    { id: "cover", label: "Cover page" },
    ...(hasSeries ? [{ id: "performance", label: "Returns backtest" }] : []),
    { id: "portfolio-overview", label: "Portfolio overview" },
    { id: "recommendations", label: "Recommendations" },
    { id: "asset-allocation", label: "Asset allocation" },
    { id: "exposure", label: "Exposure" },
  ];
}

export function leafSectionIds(sections: ReportPreviewSection[]): string[] {
  return sections.flatMap((section) =>
    section.children?.length ? leafSectionIds(section.children) : [section.id]
  );
}
